import BuildingIcon from 'assets/icons/reports-building.svg';
import BarChartIcon from 'assets/icons/reports-chart-bar.svg';
import BubbleChartIcon from 'assets/icons/reports-chart-bubble.svg';
import CustomIcon from 'assets/icons/reports-custom.svg';
import HappyFaceIcon from 'assets/icons/reports-face-happy.svg';
import GlobeIcon from 'assets/icons/reports-globe.svg';
import HeartIcon from 'assets/icons/reports-heart.svg';
import PersonDancingIcon from 'assets/icons/reports-person-dancing.svg';
import PersonCheckMarkIcon from 'assets/icons/reports-person-checkmark.svg';
import PersonIcon from 'assets/icons/reports-person.svg';
import WorldIcon from 'assets/icons/reports-world.svg';
import config from 'config';
import { Privileges } from 'utils/privileges';

const isSandbox = !config.isProduction;

interface SingleBoxInterface {
  icon: SVGElement;
  family?: string;
  type: string;
  title: string;
  text: string;
  comparison: boolean;
  privilegeNeeded: string[];
  featuresNeeded?: string[];
  entity: 'donations' | 'volunteerTimes' | 'volunteerTimeOff';
  isEnabled: boolean;
}

const reports: SingleBoxInterface[] = [
  {
    icon: BubbleChartIcon,
    family: 'donations',
    type: 'summary',
    title: 'Donation Summary',
    text: 'View and export all of your key donation metrics on one simple page.',
    comparison: true,
    privilegeNeeded: [Privileges.DONATION_VIEW],
    entity: 'donations',
    isEnabled: true,
  },
  // @FIXME-RT: Make this demoware real with https://github.com/joindeed/api/pull/1011
  {
    icon: PersonIcon,
    family: 'volunteering',
    type: 'summary',
    title: 'Volunteer Summary',
    text: 'View and export all of your key volunteer metrics on one simple page.',
    comparison: true,
    privilegeNeeded: [Privileges.VOLUNTEERTIME],
    featuresNeeded: ['volunteering'],
    entity: 'volunteerTimes',
    isEnabled: isSandbox,
  },
  {
    icon: BarChartIcon,
    family: 'donations',
    type: 'campaigns',
    title: 'Campaign and Fundraiser Spotlight',
    text: 'Monitor the results and performance of your Campaigns and Fundraisers.',
    comparison: false,
    privilegeNeeded: [Privileges.DONATION_VIEW],
    entity: 'donations',
    isEnabled: true,
  },
  {
    icon: WorldIcon,
    family: 'donations',
    type: 'sdgs',
    title: 'UN SDG Spotlight',
    text: 'Track your impact and engagement across the UN Sustainable Development Goals.',
    comparison: false,
    privilegeNeeded: [Privileges.DONATION_VIEW],
    entity: 'donations',
    isEnabled: true,
  },
  {
    icon: GlobeIcon,
    family: 'donations',
    type: 'causes',
    title: 'Cause Area Spotlight',
    text: 'Track your impact and engagement across your Featured Cause Areas.',
    comparison: false,
    privilegeNeeded: [Privileges.DONATION_VIEW],
    entity: 'donations',
    isEnabled: true,
  },
  {
    icon: HappyFaceIcon,
    family: 'donations',
    type: 'donors',
    title: 'Employee Giving Spotlight',
    text: 'Explore and analyze the composition and distribution of employee giving.',
    comparison: false,
    privilegeNeeded: [Privileges.DONATION_VIEW],
    entity: 'donations',
    isEnabled: true,
  },
  {
    icon: HeartIcon,
    family: 'donations',
    type: 'nonprofits',
    title: 'Nonprofit Spotlight',
    text: 'Analyze and quantify your impact on partner nonprofits.',
    comparison: false,
    privilegeNeeded: [Privileges.DONATION_VIEW],
    entity: 'donations',
    isEnabled: true,
  },
  // @FIXME-RT: Make this demoware real with https://github.com/joindeed/api/pull/1011
  {
    icon: PersonDancingIcon,
    family: 'volunteering',
    type: 'volunteers',
    title: 'Employee Volunteer Spotlight',
    text: 'Explore and analyze the composition and distribution of employee volunteering.',
    comparison: false,
    privilegeNeeded: [Privileges.VOLUNTEERTIME],
    featuresNeeded: ['volunteering'],
    entity: 'volunteerTimes',
    isEnabled: isSandbox,
  },
  {
    icon: PersonCheckMarkIcon,
    family: 'volunteering',
    type: 'time-off',
    title: 'Volunteering Time Off',
    text: 'See and manage VTO for employees in your company.',
    comparison: false,
    privilegeNeeded: [Privileges.VOLUNTEER_TIME_OFF_VIEW],
    featuresNeeded: ['volunteerTimeOff'],
    entity: 'volunteerTimeOff',
    isEnabled: isSandbox,
  },
  // @FIXME-RT: Make this demoware real with https://github.com/joindeed/api/pull/1011
  {
    icon: BuildingIcon,
    family: 'volunteering',
    type: 'nonprofits',
    title: 'Nonprofit Volunteer Spotlight',
    text: 'Analyze and quantify your impact on partner nonprofits.',
    comparison: false,
    privilegeNeeded: [Privileges.VOLUNTEERTIME],
    featuresNeeded: ['volunteering'],
    entity: 'volunteerTimes',
    isEnabled: isSandbox,
  },
  {
    icon: CustomIcon,
    family: 'donations',
    type: 'xlsx',
    title: 'Custom Reports',
    text: 'View your custom report templates.',
    comparison: true,
    privilegeNeeded: [],
    entity: 'donations',
    isEnabled: isSandbox,
  },
].filter((report: SingleBoxInterface) => report.isEnabled);

const getSingleReport = (family: string, type: string): SingleBoxInterface | null => {
  const result = reports.find(
    (item: SingleBoxInterface) => item.type === type && item.family === family
  );
  return result || null;
};

export { SingleBoxInterface, reports, getSingleReport };
